import { useEffect, useState } from 'react'

/** Opt a control in by putting this on it. The container carries the hook. */
export const PRESS_TARGET_ATTRIBUTE = 'data-press'

/** What the mixins in `mixins.scss` draw the press against. */
export const PRESSED_ATTRIBUTE = 'data-pressed'

/**
 * How long the mark stays down at minimum.
 *
 * A tap on a phone is routinely shorter than this, and a press that goes on and off inside
 * one frame is a press the hand never saw. Roughly what UIKit holds a highlight for.
 */
const MIN_VISIBLE_MS = 120

/** Travel that makes the touch a scroll, at which point it was never a press. */
const CANCEL_SLOP_PX = 10

/** Attach to the element that CONTAINS the controls — the listeners are delegated. */
type PressRef = (node: HTMLElement | null) => void

const findPressTarget = (target: EventTarget | null): HTMLElement | null =>
  target instanceof Element ? target.closest<HTMLElement>(`[${PRESS_TARGET_ATTRIBUTE}]`) : null

/**
 * Press feedback for touch controls, driven from `pointerdown` instead of `:active`.
 *
 * `:active` is the wrong tool here, which is why the tab bar still felt dead after being
 * given one. Blink withholds the active state until it can tell a tap from the start of a
 * scroll, then drops it the instant the finger lifts — so a quick tap can take and lose the
 * state within a single frame and paint nothing. Chrome's own tap highlight used to cover
 * for that and is switched off in `global.scss`, because a grey flash reads as a web page.
 *
 * Owning the press answers both halves: down is `pointerdown`, which waits for nothing, and
 * the mark is held for `MIN_VISIBLE_MS` so the shortest tap is still something you see. The
 * attribute is written straight to the DOM rather than through state, so it paints on the
 * next frame even while React is busy rendering the route the tap asked for — which is
 * exactly the moment the reassurance is needed.
 *
 * A pointer that travels, or that the system takes away, releases with no minimum: nothing
 * was chosen, so there is nothing to acknowledge.
 */
export const useTouchPress = (): PressRef => {
  const [container, setContainer] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (!container) return

    let pressed: HTMLElement | null = null
    let origin: { x: number; y: number } | null = null
    let holdTimer: ReturnType<typeof setTimeout> | null = null
    let isReleased = false
    let isHoldElapsed = false

    const finish = () => {
      if (holdTimer) {
        clearTimeout(holdTimer)
        holdTimer = null
      }
      pressed?.removeAttribute(PRESSED_ATTRIBUTE)
      pressed = null
      origin = null
    }

    const handlePointerDown = (event: PointerEvent) => {
      // A mouse has hover, which is the feedback it is supposed to get.
      if (event.pointerType === 'mouse') return

      const target = findPressTarget(event.target)
      if (!target) return

      finish()
      pressed = target
      origin = { x: event.clientX, y: event.clientY }
      isReleased = false
      isHoldElapsed = false
      target.setAttribute(PRESSED_ATTRIBUTE, '')

      holdTimer = setTimeout(() => {
        holdTimer = null
        isHoldElapsed = true
        if (isReleased) finish()
      }, MIN_VISIBLE_MS)
    }

    const handlePointerMove = (event: PointerEvent) => {
      if (!pressed || !origin) return
      if (Math.hypot(event.clientX - origin.x, event.clientY - origin.y) > CANCEL_SLOP_PX) finish()
    }

    const handlePointerUp = () => {
      if (!pressed) return
      isReleased = true
      if (isHoldElapsed) finish()
    }

    // Passive throughout: this only ever reads the gesture, and a listener that can block
    // is a listener the compositor has to wait for before it scrolls.
    const options: AddEventListenerOptions = { passive: true }
    container.addEventListener('pointerdown', handlePointerDown, options)
    container.addEventListener('pointermove', handlePointerMove, options)
    container.addEventListener('pointerup', handlePointerUp, options)
    container.addEventListener('pointercancel', finish, options)

    return () => {
      container.removeEventListener('pointerdown', handlePointerDown, options)
      container.removeEventListener('pointermove', handlePointerMove, options)
      container.removeEventListener('pointerup', handlePointerUp, options)
      container.removeEventListener('pointercancel', finish, options)
      finish()
    }
  }, [container])

  return setContainer
}

export default useTouchPress
